import { Alert, Snackbar, styled, Typography, useTheme } from '@mui/material';
import React from 'react';

import { colors } from '../theme/theme';
import CommonIconAvatar from '../ui-components/CommonIconAvatar';

interface IContainerProps {
    open: boolean;
    message?: string;
    severity?: 'error' | 'warning' | 'info' | 'success';
    handleClose: (value: boolean) => void;
}
export const ToastUI = styled(Snackbar)(({ theme }) => ({
    maxWidth: 384,
    width: 'fit-content',
    [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
    },
    '& .MuiPaper-root': {
        borderRadius: 8,
        padding: 12,
        gap: 8,
        alignItems: 'center',
        boxShadow: theme.shadows[4],
        '& .MuiAlert-message': {
            padding: 0,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
        },
        '& .MuiAlert-action': {
            padding: 0,
        },
        '&.MuiAlert-colorError': {
            background: colors.errorPreset[10],
        },
        '&.MuiAlert-colorSuccess': {
            background: colors.successPreset[50],
        },
        '&.MuiAlert-colorInfo': {
            background: colors.bluePreset[30],
        },
        '&.MuiAlert-colorWarning': {
            background: colors.warningPreset[50],
        },
    },
}));

function getToastIconProps(severity: string) {
    switch (severity) {
        case 'error':
            return { icon: 'icon-error', iconBgColor: colors.errorPreset[40] };
        case 'info':
            return { icon: 'icon-info-filled', iconBgColor: colors.bluePreset[30] };
        case 'warning':
            return { icon: 'icon-warning', iconBgColor: colors.warningPreset[70] };
        default:
            return { icon: 'icon-check', iconBgColor: colors.successPreset[30] };
    }
}

export const Toast = (props: IContainerProps) => {
    const theme = useTheme();
    const { open, message, severity, handleClose } = props;
    const { icon, iconBgColor } = getToastIconProps(String(severity ?? 'success'));
    return (
        <ToastUI
            open={open}
            onClose={() => handleClose(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            autoHideDuration={5000}
            sx={{
                zIndex: 9999,
            }}
        >
            <Alert
                data-testid="toast-message"
                variant="filled"
                severity={severity}
                onClose={() => handleClose(false)}
                sx={{ width: '100%' }}
                icon={false}
            >
                <CommonIconAvatar
                    icon={icon}
                    iconColor={colors.fullWhite}
                    iconBorderRadius={50}
                    iconPadding={1.25}
                    iconSize={18}
                    iconBgColor={iconBgColor}
                />

                <Typography variant="body2" fontFamily={theme.typography.fontFamily} sx={{ whiteSpace: 'pre-line' }}>
                    {message}
                </Typography>
            </Alert>
        </ToastUI>
    );
};
